import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Badge } from "./ui/badge";
import { Plus, Edit, Trash2, Download, Table as TableIcon, BarChart3, ChevronDown, ChevronRight, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, differenceInDays, eachDayOfInterval, startOfMonth, endOfMonth, addDays, startOfWeek, addWeeks, isWeekend } from "date-fns";

export default function LookForwardPlanner() {
  const { user } = useContext(AuthContext);
  
  // Check if user is view_only (read-only mode)
  const isViewOnly = user?.role === "view_only";
  
  const [activities, setActivities] = useState([]);
  const [sites, setSites] = useState([]);
  const [trades, setTrades] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("gantt"); // gantt, table, calendar
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddContractorDialog, setShowAddContractorDialog] = useState(false);
  const [showAddTradeDialog, setShowAddTradeDialog] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [filterTrade, setFilterTrade] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [newContractor, setNewContractor] = useState("");
  const [newTrade, setNewTrade] = useState("");
  const [collapsedSites, setCollapsedSites] = useState(new Set());
  const [weekOffset, setWeekOffset] = useState(0); // For timeline navigation
  const [pdfStartDate, setPdfStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [pdfEndDate, setPdfEndDate] = useState(format(addWeeks(new Date(), 4), "yyyy-MM-dd"));
  const [pdfSiteFilter, setPdfSiteFilter] = useState("all");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  const [newActivity, setNewActivity] = useState({
    activity_name: "",
    contractor: "",
    trade: "",
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: format(addDays(new Date(), 7), "yyyy-MM-dd"),
    duration_days: 7,
    progress_percent: 0,
    status: "planned",
    notes: ""
  });

  useEffect(() => {
    loadSites();
    loadTrades();
    loadCompanies();
  }, []);

  useEffect(() => {
    loadActivities();
  }, [filterTrade, filterStatus]);

  const loadSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data);
        if (data.length > 0) {
          setSelectedSiteId(data[0].id);
        }
      }
    } catch (error) {
      console.error("Error loading sites:", error);
    }
  };

  const loadTrades = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/trades`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrades(data);
      }
    } catch (error) {
      console.error("Error loading trades:", error);
    }
  };

  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/companies`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error("Error loading companies:", error);
    }
  };

  const loadActivities = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let url = `${API}/planner-activities`;
      const params = [];
      if (filterTrade !== "all") params.push(`trade=${filterTrade}`);
      if (filterStatus !== "all") params.push(`status=${filterStatus}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await fetch(url, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActivities(data);
      }
    } catch (error) {
      console.error("Error loading activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/planner-activities`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...newActivity,
          site_id: selectedSiteId
        })
      });

      if (response.ok) {
        toast.success("Activity added successfully!");
        setShowAddDialog(false);
        setNewActivity({
          activity_name: "",
          contractor: "",
          trade: "",
          start_date: format(new Date(), "yyyy-MM-dd"),
          end_date: format(addDays(new Date(), 7), "yyyy-MM-dd"),
          duration_days: 7,
          progress_percent: 0,
          status: "planned",
          notes: ""
        });
        loadActivities();
      } else {
        toast.error("Failed to add activity");
      }
    } catch (error) {
      console.error("Error adding activity:", error);
      toast.error("Failed to add activity");
    }
  };

  const handleUpdateActivity = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/planner-activities/${editingActivity.id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(editingActivity)
      });

      if (response.ok) {
        toast.success("Activity updated successfully!");
        setShowEditDialog(false);
        setEditingActivity(null);
        loadActivities();
      } else {
        toast.error("Failed to update activity");
      }
    } catch (error) {
      console.error("Error updating activity:", error);
      toast.error("Failed to update activity");
    }
  };

  const handleInlineUpdate = async (activityId, field, value) => {
    try {
      const token = localStorage.getItem("token");
      const activity = activities.find(a => a.id === activityId);
      
      if (!activity) return;
      
      let updatedActivity = { ...activity, [field]: value };
      
      // If start_date or duration_days changed, recalculate end_date using BUSINESS DAYS
      if (field === 'start_date' || field === 'duration_days') {
        let startDate = field === 'start_date' ? parseISO(value) : parseISO(activity.start_date);
        const duration = field === 'duration_days' ? parseInt(value) : activity.duration_days;
        
        // If start date is on weekend, move it to next Monday
        while (isWeekend(startDate)) {
          startDate = addDays(startDate, 1);
        }
        
        // Update start date if it was adjusted
        updatedActivity.start_date = format(startDate, 'yyyy-MM-dd');
        
        // Calculate end date by adding business days (skip weekends)
        let endDate = new Date(startDate);
        let businessDaysAdded = 1; // Start date counts as day 1
        
        while (businessDaysAdded < duration) {
          endDate = addDays(endDate, 1);
          // Only count weekdays
          if (!isWeekend(endDate)) {
            businessDaysAdded++;
          }
        }
        
        updatedActivity.end_date = format(endDate, 'yyyy-MM-dd');
        updatedActivity.duration_days = duration;
      }
      
      const response = await fetch(`${API}/planner-activities/${activityId}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(updatedActivity)
      });

      if (response.ok) {
        loadActivities();
      } else {
        toast.error("Failed to update activity");
      }
    } catch (error) {
      console.error("Error updating activity:", error);
      toast.error("Failed to update activity");
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm("Are you sure you want to delete this activity?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/planner-activities/${activityId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Activity deleted successfully!");
        loadActivities();
      } else {
        toast.error("Failed to delete activity");
      }
    } catch (error) {
      console.error("Error deleting activity:", error);
      toast.error("Failed to delete activity");
    }
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    return differenceInDays(parseISO(endDate), parseISO(startDate)) + 1;
  };

  const handleDateChange = (field, value, isNew = false) => {
    const target = isNew ? newActivity : editingActivity;
    const setter = isNew ? setNewActivity : setEditingActivity;

    const updated = { ...target, [field]: value };
    
    // Auto-calculate end date from start date + duration (business days only, excluding weekends)
    if (field === "start_date" || field === "duration_days") {
      if (updated.start_date && updated.duration_days) {
        let startDate = parseISO(updated.start_date);
        const durationDays = parseInt(updated.duration_days);
        
        // If start date is on weekend, move it to next Monday
        while (isWeekend(startDate)) {
          startDate = addDays(startDate, 1);
        }
        
        // Update start date if it was adjusted
        updated.start_date = format(startDate, "yyyy-MM-dd");
        
        // Calculate end date by adding business days (skip weekends)
        let endDate = new Date(startDate);
        let businessDaysAdded = 1; // Start date counts as day 1
        
        while (businessDaysAdded < durationDays) {
          endDate = addDays(endDate, 1);
          // Only count weekdays
          if (!isWeekend(endDate)) {
            businessDaysAdded++;
          }
        }
        
        updated.end_date = format(endDate, "yyyy-MM-dd");
      }
    }

    setter(updated);
  };

  const handleAddContractor = async () => {
    if (!newContractor.trim()) {
      toast.error("Contractor name is required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/companies`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: newContractor,
          company_type: "contractor"
        })
      });

      if (response.ok) {
        toast.success("Contractor added successfully!");
        setNewContractor("");
        setShowAddContractorDialog(false);
        loadCompanies();
        // Set the new contractor as selected
        setNewActivity({...newActivity, contractor: newContractor});
      } else {
        toast.error("Failed to add contractor");
      }
    } catch (error) {
      console.error("Error adding contractor:", error);
      toast.error("Failed to add contractor");
    }
  };

  const handleAddTrade = async () => {
    if (!newTrade.trim()) {
      toast.error("Trade name is required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/trades`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: newTrade
        })
      });

      if (response.ok) {
        toast.success("Trade added successfully!");
        setNewTrade("");
        setShowAddTradeDialog(false);
        loadTrades();
        // Set the new trade as selected
        setNewActivity({...newActivity, trade: newTrade});
      } else {
        toast.error("Failed to add trade");
      }
    } catch (error) {
      console.error("Error adding trade:", error);
      toast.error("Failed to add trade");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-red-500 text-white";
      case "in_progress": return "bg-blue-100 text-blue-800";
      case "delayed": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // Sort activities by Contractor, then Trade, then Activity Name
  const sortActivities = (activitiesList) => {
    return [...activitiesList].sort((a, b) => {
      // Primary sort: Contractor (alphabetically)
      const contractorA = (a.contractor || "").toLowerCase();
      const contractorB = (b.contractor || "").toLowerCase();
      if (contractorA < contractorB) return -1;
      if (contractorA > contractorB) return 1;

      // Secondary sort: Start Date (earliest first)
      const dateA = new Date(a.start_date);
      const dateB = new Date(b.start_date);
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;

      // Tertiary sort: Activity Name (alphabetically)
      const activityA = (a.activity_name || "").toLowerCase();
      const activityB = (b.activity_name || "").toLowerCase();
      if (activityA < activityB) return -1;
      if (activityA > activityB) return 1;

      return 0;
    });
  };

  // Group activities by site
  const groupActivitiesBySite = (activitiesList) => {
    const grouped = {};
    activitiesList.forEach(activity => {
      const siteId = activity.site_id;
      if (!grouped[siteId]) {
        grouped[siteId] = [];
      }
      grouped[siteId].push(activity);
    });
    return grouped;
  };

  // Toggle site collapse
  const toggleSiteCollapse = (siteId) => {
    const newCollapsed = new Set(collapsedSites);
    if (newCollapsed.has(siteId)) {
      newCollapsed.delete(siteId);
    } else {
      newCollapsed.add(siteId);
    }
    setCollapsedSites(newCollapsed);
  };

  // Get site name by ID
  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? site.name : "Unknown Site";
  };

  // Get 4-week range starting from Monday of current week + offset (including weekends)
  const getTimelineRange = () => {
    const today = new Date();
    const startMonday = startOfWeek(today, { weekStartsOn: 1 }); // Start from Monday
    const offsetStartMonday = addWeeks(startMonday, weekOffset);
    const endDate = addWeeks(offsetStartMonday, 4); // 4 weeks
    
    const allDates = eachDayOfInterval({ start: offsetStartMonday, end: endDate });
    // Return all days including weekends
    return allDates;
  };

  // Calculate business days between two dates (excluding weekends)
  const getBusinessDaysBetween = (startDate, endDate) => {
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    return days.filter(date => !isWeekend(date)).length;
  };

  // Navigate timeline
  const navigateTimeline = (direction) => {
    setWeekOffset(prev => direction === 'next' ? prev + 1 : prev - 1);
  };

  // Generate PDF Report
  const handleGeneratePdf = async () => {
    if (!pdfStartDate || !pdfEndDate) {
      toast.error("Please select both start and end dates for the report");
      return;
    }

    setGeneratingPdf(true);
    try {
      const token = localStorage.getItem("token");
      
      let url = `${API}/planner-activities/pdf-report?start_date=${pdfStartDate}&end_date=${pdfEndDate}`;
      if (pdfSiteFilter !== "all") {
        url += `&site_id=${pdfSiteFilter}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Download the PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Look_Forward_Planner_${pdfStartDate}_to_${pdfEndDate}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success("PDF report generated successfully!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate PDF report");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error generating PDF report");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Gantt Chart View
  const renderGanttChart = () => {
    if (activities.length === 0) {
      return <div className="text-center py-12 text-gray-500">No activities planned. Click + to add an activity.</div>;
    }

    // Filter activities by selected site if one is selected
    const filteredActivities = selectedSiteId 
      ? activities.filter(activity => activity.site_id === selectedSiteId)
      : activities;

    if (filteredActivities.length === 0) {
      return <div className="text-center py-12 text-gray-500">No activities found for the selected site.</div>;
    }

    // Group activities by site
    const groupedActivities = groupActivitiesBySite(filteredActivities);
    
    // Get 4-week weekday timeline
    const dateRange = getTimelineRange();
    const timelineStart = dateRange[0];
    const timelineEnd = dateRange[dateRange.length - 1];
    
    return (
      <div className="space-y-6">
        {/* Timeline Navigation */}
        <div className="flex items-center justify-between bg-slate-100 p-1 rounded">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigateTimeline('prev')}
            className="h-6 px-2 text-xs"
          >
            ← Prev 4W
          </Button>
          <div className="text-xs font-semibold text-slate-700">
            {format(timelineStart, 'd MMM')} - {format(timelineEnd, 'd MMM yyyy')}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigateTimeline('next')}
            className="h-6 px-2 text-xs"
          >
            Next 4W →
          </Button>
        </div>

        {Object.entries(groupedActivities).map(([siteId, siteActivities]) => {
          const sortedActivities = sortActivities(siteActivities);
          const isCollapsed = collapsedSites.has(siteId);
          const siteName = getSiteName(siteId);

          return (
            <div key={siteId} className="border rounded-lg overflow-hidden">
              {/* Site Header */}
              <div
                className="bg-slate-700 text-white p-2 flex items-center justify-between cursor-pointer hover:bg-slate-600 transition-colors"
                onClick={() => toggleSiteCollapse(siteId)}
              >
                <div className="flex items-center gap-2">
                  {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  <h3 className="font-semibold text-base">{siteName}</h3>
                  <Badge variant="secondary" className="bg-slate-500 text-white text-xs px-1 py-0">
                    {sortedActivities.length} {sortedActivities.length === 1 ? 'activity' : 'activities'}
                  </Badge>
                </div>
              </div>

              {/* Activities */}
              {!isCollapsed && (
                <div className="p-2 bg-white">
                  {/* Mobile Hint */}
                  <div className="md:hidden bg-blue-50 border border-blue-200 rounded p-2 mb-2 text-xs text-blue-800">
                    💡 Swipe horizontally to view the full timeline
                  </div>
                  
                  {/* Scrollable Gantt Container */}
                  <div className="overflow-x-auto -mx-2 px-2 pb-2">
                    <div className="min-w-[1000px] md:min-w-[1400px] relative">
                      {/* Timeline Date Header */}
                      <div className="grid" style={{ gridTemplateColumns: '120px 60px 140px 90px 45px 40px 1fr 50px' }}>
                        <div className="font-semibold text-[10px] bg-slate-100 px-0.5 leading-tight sticky left-0 z-20 border-r">Contractor</div>
                        <div className="font-semibold text-[10px] bg-slate-100 px-0.5 leading-tight sticky left-[120px] z-20 border-r">Trade</div>
                        <div className="font-semibold text-[10px] bg-slate-100 px-0.5 leading-tight sticky left-[180px] z-20 border-r">Activity</div>
                        <div className="font-semibold text-[10px] bg-slate-100 px-0.5 leading-tight sticky left-[320px] z-20 border-r">Start Date</div>
                        <div className="font-semibold text-[10px] bg-slate-100 px-0.5 leading-tight sticky left-[410px] z-20 border-r text-center">Days</div>
                        <div className="font-semibold text-[10px] bg-slate-100 px-0.5 leading-tight sticky left-[455px] z-20 border-r text-center">%</div>
                        <div className="bg-slate-100">
                          {/* Date row */}
                          <div className="flex">
                            {dateRange.map((date, idx) => {
                              const dayOfWeek = format(date, 'EEE');
                              const isWeekendDay = dayOfWeek === 'Sat' || dayOfWeek === 'Sun';
                              return (
                                <div 
                                  key={idx} 
                                  className={`flex-1 text-center border-r last:border-r-0 ${isWeekendDay ? 'bg-gray-400' : ''}`}
                                  style={{ minWidth: '28px' }}
                                >
                                  <div className={`text-[8px] font-semibold leading-none ${isWeekendDay ? 'text-white' : ''}`}>{format(date, 'd/M')}</div>
                                  <div className={`text-[8px] leading-none ${isWeekendDay ? 'text-white' : ''}`}>{dayOfWeek}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="font-semibold text-[10px] bg-slate-100 px-0.5 text-center leading-tight">Act</div>
                      </div>

                      {/* Activities Container with Grid Lines */}
                      <div className="relative" style={{ gridTemplateColumns: '120px 60px 140px 90px 45px 40px 1fr 50px' }}>
                        {/* Vertical Grid Lines - positioned to match timeline area, excluding actions column */}
                        <div className="absolute top-0 bottom-0 pointer-events-none grid" style={{ left: 0, right: '50px', gridTemplateColumns: '120px 60px 140px 90px 45px 40px 1fr' }}>
                          <div></div>
                          <div></div>
                          <div></div>
                          <div></div>
                          <div></div>
                          <div></div>
                          <div className="flex">
                            {dateRange.map((date, idx) => {
                              const dayOfWeek = format(date, 'EEE');
                              const isWeekendDay = dayOfWeek === 'Sat' || dayOfWeek === 'Sun';
                              return (
                                <div 
                                  key={idx}
                                  className={`flex-1 border-r ${isWeekendDay ? 'bg-gray-300 opacity-60' : ''}`}
                                  style={{ minWidth: '28px' }}
                                ></div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Activity Rows */}
                        {(() => {
                          // Filter visible activities - show if:
                          // 1. Activity dates overlap with timeline range, OR
                          // 2. Activity is not 100% complete (keep incomplete tasks visible)
                          const visibleActivities = sortedActivities.filter(act => {
                            const start = parseISO(act.start_date);
                            const end = parseISO(act.end_date);
                            const isInDateRange = !(end < timelineStart || start > timelineEnd);
                            const isNotComplete = (act.progress_percent || 0) < 100;
                            return isInDateRange || isNotComplete;
                          });
                          
                          return visibleActivities.map((activity, visibleIndex) => {
                            const startDate = parseISO(activity.start_date);
                            const endDate = parseISO(activity.end_date);

                            // Calculate visible date range for this activity
                            const visibleStartDate = startDate < timelineStart ? timelineStart : startDate;
                            const visibleEndDate = endDate > timelineEnd ? timelineEnd : endDate;

                            // Check if activity is outside date range but still visible because incomplete
                            const isInDateRange = !(endDate < timelineStart || startDate > timelineEnd);
                            const isIncompleteOutsideRange = !isInDateRange && (activity.progress_percent || 0) < 100;

                            // Detect contractor change for visual separation (using visible activities only)
                            const previousActivity = visibleIndex > 0 ? visibleActivities[visibleIndex - 1] : null;
                            const currentContractor = activity.contractor || "";
                            const previousContractor = previousActivity ? (previousActivity.contractor || "") : null;
                            const isNewContractor = previousContractor !== null && currentContractor !== previousContractor;
                        
                        // Determine contractor group index for alternating background
                        let contractorGroupIndex = 0;
                        const uniqueContractors = [];
                        sortedActivities.forEach(act => {
                          const contractor = act.contractor || "";
                          if (!uniqueContractors.includes(contractor)) {
                            uniqueContractors.push(contractor);
                          }
                        });
                        contractorGroupIndex = uniqueContractors.indexOf(currentContractor);
                        const isEvenContractorGroup = contractorGroupIndex % 2 === 0;
                        // Use red background for incomplete tasks outside date range
                        const rowBgColor = isIncompleteOutsideRange 
                          ? "bg-red-50" 
                          : (isEvenContractorGroup ? "bg-white" : "bg-gray-100");

                        return (
                          <div key={activity.id} className={`grid items-center border-b relative ${rowBgColor} ${isNewContractor ? 'border-t-[3px] border-t-slate-800' : ''} ${isIncompleteOutsideRange ? 'border-l-4 border-l-red-600' : ''}`} style={{ gridTemplateColumns: '120px 60px 140px 90px 45px 40px 1fr 50px', minHeight: '28px', zIndex: 1 }}>
                              <div className={`text-[10px] truncate px-0.5 py-px sticky left-0 z-10 ${rowBgColor} border-r ${isIncompleteOutsideRange ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
                                {isIncompleteOutsideRange && <span className="text-red-600 mr-0.5">⚠</span>}
                                {activity.contractor || "-"}
                              </div>
                              <div className={`px-0.5 py-px sticky left-[120px] z-10 ${rowBgColor} border-r flex items-center`}>
                                <Badge className="text-[10px] px-0.5 py-0 leading-none h-3.5">{activity.trade}</Badge>
                              </div>
                              <div className={`text-[10px] px-0.5 py-px sticky left-[180px] z-10 ${rowBgColor} border-r line-clamp-2 leading-snug`}>
                                {activity.activity_name}
                              </div>
                              <div className={`sticky left-[320px] z-10 ${rowBgColor} border-r px-0.5 py-px`}>
                                {!isViewOnly ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button className="text-[10px] w-full text-left hover:bg-blue-50 px-0.5 py-px rounded">
                                        {format(parseISO(activity.start_date), "dd/MM/yyyy")}
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                      <Calendar
                                        mode="single"
                                        selected={parseISO(activity.start_date)}
                                        onSelect={(date) => {
                                          if (date) {
                                            const newDate = format(date, "yyyy-MM-dd");
                                            handleInlineUpdate(activity.id, 'start_date', newDate);
                                          }
                                        }}
                                        initialFocus
                                      />
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <span className="text-[10px]">{format(parseISO(activity.start_date), "dd/MM/yyyy")}</span>
                                )}
                              </div>
                              <div className={`sticky left-[410px] z-10 ${rowBgColor} border-r px-0.5 py-px`}>
                                {!isViewOnly ? (
                                  <input
                                    type="number"
                                    defaultValue={activity.duration_days}
                                    onBlur={(e) => {
                                      if (e.target.value !== String(activity.duration_days)) {
                                        handleInlineUpdate(activity.id, 'duration_days', e.target.value);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.target.blur();
                                      }
                                    }}
                                    className={`text-[10px] w-full border-none outline-none bg-transparent p-0 text-center hover:bg-blue-50 rounded`}
                                    min="1"
                                  />
                                ) : (
                                  <span className="text-[10px]">{activity.duration_days}</span>
                                )}
                              </div>
                              <div className={`sticky left-[455px] z-10 ${rowBgColor} border-r px-0.5 py-px`}>
                                {!isViewOnly ? (
                                  <input
                                    type="number"
                                    defaultValue={activity.progress_percent || 0}
                                    onBlur={(e) => {
                                      const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                      if (value !== activity.progress_percent) {
                                        handleInlineUpdate(activity.id, 'progress_percent', String(value));
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.target.blur();
                                      }
                                    }}
                                    className={`text-[10px] w-full border-none outline-none bg-transparent p-0 text-center hover:bg-blue-50 rounded`}
                                    min="0"
                                    max="100"
                                  />
                                ) : (
                                  <span className="text-[10px]">{activity.progress_percent || 0}%</span>
                                )}
                              </div>
                              <div className="relative h-3 overflow-hidden">
                                {/* Timeline grid background for alignment */}
                                <div className="absolute inset-0 flex">
                                  {dateRange.map((date, idx) => (
                                    <div key={idx} className="flex-1" style={{ minWidth: '28px' }}></div>
                                  ))}
                                </div>
                                
                                {/* Activity bar positioned over grid */}
                                <div className="absolute inset-0 flex">
                                  {dateRange.map((date, dateIdx) => {
                                    const currentDate = date;
                                    const isInActivity = currentDate >= visibleStartDate && currentDate <= visibleEndDate;
                                    const isWeekendDay = isWeekend(currentDate);
                                    
                                    if (!isInActivity) {
                                      return <div key={dateIdx} className="flex-1" style={{ minWidth: '28px' }}></div>;
                                    }
                                    
                                    const isFirstDay = format(currentDate, 'yyyy-MM-dd') === format(visibleStartDate, 'yyyy-MM-dd');
                                    const isLastDay = format(currentDate, 'yyyy-MM-dd') === format(visibleEndDate, 'yyyy-MM-dd');
                                    
                                    // Calculate if this day should show progress
                                    const percentComplete = activity.progress_percent || 0;
                                    const daysSinceStart = differenceInDays(currentDate, visibleStartDate);
                                    const totalActivityDays = differenceInDays(visibleEndDate, visibleStartDate) + 1;
                                    const progressInDays = (percentComplete / 100) * totalActivityDays;
                                    const showProgress = daysSinceStart < progressInDays;
                                    
                                    return (
                                      <div 
                                        key={dateIdx} 
                                        className={`flex-1 relative ${isFirstDay ? 'rounded-l' : ''} ${isLastDay ? 'rounded-r' : ''}`}
                                        style={{ minWidth: '28px' }}
                                      >
                                        {/* Base bar */}
                                        <div className="absolute inset-0 bg-blue-200 border-y border-blue-300" 
                                             style={{ 
                                               borderLeftWidth: isFirstDay ? '1px' : '0',
                                               borderRightWidth: isLastDay ? '1px' : '0'
                                             }}>
                                          
                                          {/* Progress line - red horizontal line through middle of bar */}
                                          {showProgress && percentComplete > 0 && (
                                            <div className="absolute left-0 right-0 h-0.5 bg-red-600 z-10" style={{ top: '50%', transform: 'translateY(-50%)' }}></div>
                                          )}
                                          
                                          {/* Weekend overlay */}
                                          {isWeekendDay && (
                                            <div className="absolute inset-0 bg-gray-700 opacity-70"></div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              {!isViewOnly && (
                                <div className="flex justify-center gap-0.5 relative z-10" style={{ pointerEvents: 'auto' }}>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-5 w-5 p-0 cursor-pointer hover:bg-gray-200"
                                    style={{ pointerEvents: 'auto' }}
                                    onClick={() => {
                                      setEditingActivity(activity);
                                      setShowEditDialog(true);
                                    }}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-5 w-5 p-0 cursor-pointer hover:bg-gray-200"
                                    style={{ pointerEvents: 'auto' }}
                                    onClick={() => handleDeleteActivity(activity.id)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Table View
  const renderTableView = () => {
    if (activities.length === 0) {
      return <div className="text-center py-12 text-gray-500">No activities planned. Click + to add an activity.</div>;
    }

    // Filter activities by selected site if one is selected
    const filteredActivities = selectedSiteId 
      ? activities.filter(activity => activity.site_id === selectedSiteId)
      : activities;

    if (filteredActivities.length === 0) {
      return <div className="text-center py-12 text-gray-500">No activities found for the selected site.</div>;
    }

    // Group activities by site
    const groupedActivities = groupActivitiesBySite(filteredActivities);

    return (
      <div className="space-y-6">
        {Object.entries(groupedActivities).map(([siteId, siteActivities]) => {
          const sortedActivities = sortActivities(siteActivities);
          const isCollapsed = collapsedSites.has(siteId);
          const siteName = getSiteName(siteId);

          return (
            <div key={siteId} className="border rounded-lg overflow-hidden">
              {/* Site Header */}
              <div
                className="bg-slate-700 text-white p-2 flex items-center justify-between cursor-pointer hover:bg-slate-600 transition-colors"
                onClick={() => toggleSiteCollapse(siteId)}
              >
                <div className="flex items-center gap-2">
                  {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  <h3 className="font-semibold text-base">{siteName}</h3>
                  <Badge variant="secondary" className="bg-slate-500 text-white text-xs px-1 py-0">
                    {sortedActivities.length} {sortedActivities.length === 1 ? 'activity' : 'activities'}
                  </Badge>
                </div>
              </div>

              {/* Activities Table */}
              {!isCollapsed && (
                <div className="p-2 bg-white">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-2 py-1 text-left">Contractor</th>
                          <th className="px-2 py-1 text-left">Trade</th>
                          <th className="px-2 py-1 text-left">Activity</th>
                          <th className="px-2 py-1 text-left">Start</th>
                          <th className="px-2 py-1 text-left">End</th>
                          <th className="px-2 py-1 text-center">Dur</th>
                          <th className="px-2 py-1 text-center">Prog</th>
                          <th className="px-2 py-1 text-left">Status</th>
                          <th className="px-2 py-1 text-center">Act</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedActivities.map((activity) => (
                          <tr key={activity.id} className="border-b hover:bg-gray-50">
                            <td className="px-2 py-1 truncate max-w-[100px]">{activity.contractor || "-"}</td>
                            <td className="px-2 py-1">
                              <Badge className="text-[9px] px-1 py-0">{activity.trade}</Badge>
                            </td>
                            <td className="px-2 py-1 truncate max-w-[150px]">{activity.activity_name}</td>
                            <td className="px-2 py-1">{format(parseISO(activity.start_date), "dd MMM")}</td>
                            <td className="px-2 py-1">{format(parseISO(activity.end_date), "dd MMM")}</td>
                            <td className="px-2 py-1 text-center">{activity.duration_days}d</td>
                            <td className="px-2 py-1 text-center">{activity.progress_percent}%</td>
                            <td className="px-2 py-1">
                              <Badge className={`${getStatusColor(activity.status)} text-[9px] px-1 py-0`}>
                                {activity.status}
                              </Badge>
                            </td>
                            <td className="px-2 py-1 text-center">
                              {!isViewOnly && (
                                <div className="flex justify-center gap-0.5">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-4 w-4 p-0"
                                    onClick={() => {
                                      setEditingActivity(activity);
                                      setShowEditDialog(true);
                                    }}
                                  >
                                    <Edit className="w-2.5 h-2.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-4 w-4 p-0"
                                    onClick={() => handleDeleteActivity(activity.id)}
                                  >
                                    <Trash2 className="w-2.5 h-2.5" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-2 md:p-6 max-w-[98%] mx-auto">
      {/* Responsive Header */}
      <div className="bg-white border rounded-lg p-2 md:p-3 mb-3">
        {/* Title */}
        <h1 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">Look Forward Planner</h1>
        
        {/* Filters - Stack on mobile, row on desktop */}
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-3">
          {/* Site Filter */}
          <div className="flex items-center gap-1 flex-1 md:flex-initial">
            <Label className="text-xs whitespace-nowrap">Site:</Label>
            <Select value={selectedSiteId || "all"} onValueChange={(value) => setSelectedSiteId(value === "all" ? null : value)}>
              <SelectTrigger className="h-8 md:h-6 text-xs flex-1 md:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {sites.map(site => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Trade Filter */}
          <div className="flex items-center gap-1 flex-1 md:flex-initial">
            <Label className="text-xs whitespace-nowrap">Trade:</Label>
            <Select value={filterTrade} onValueChange={setFilterTrade}>
              <SelectTrigger className="h-8 md:h-6 text-xs flex-1 md:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Trades</SelectItem>
                {trades.map(trade => (
                  <SelectItem key={trade.id} value={trade.name}>{trade.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-1 flex-1 md:flex-initial">
            <Label className="text-xs whitespace-nowrap">Status:</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 md:h-6 text-xs flex-1 md:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="delayed">Delayed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* View Mode Toggle - Hidden on mobile */}
          <div className="hidden md:flex gap-1 border-l pl-3">
            <Button
              variant={viewMode === "gantt" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("gantt")}
              className="h-6 px-2 text-xs"
            >
              <BarChart3 className="w-3 h-3 mr-1" />
              Gantt
            </Button>
            <Button
              variant={viewMode === "table" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("table")}
              className="h-6 px-2 text-xs"
            >
              <TableIcon className="w-3 h-3 mr-1" />
              Table
            </Button>
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
          {/* Add Activity Button */}
          {!isViewOnly && (
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-blue-600 hover:bg-blue-700 h-10 sm:h-8 px-3 text-sm sm:text-xs"
            >
              <Plus className="w-4 h-4 sm:w-3 sm:h-3 mr-1" />
              Add Activity
            </Button>
          )}

          {/* PDF Controls - Stack on mobile */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs font-semibold hidden sm:block">PDF:</Label>
              <Select value={pdfSiteFilter} onValueChange={setPdfSiteFilter}>
                <SelectTrigger className="h-10 sm:h-6 text-xs flex-1 sm:w-28">
                  <SelectValue placeholder="Site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 sm:h-6 justify-start text-xs px-2 flex-1 sm:w-28">
                    <CalendarIcon className="mr-1 h-3 w-3" />
                    <span className="hidden sm:inline">{pdfStartDate ? format(parseISO(pdfStartDate), "dd/MM/yyyy") : "Start"}</span>
                    <span className="sm:hidden">{pdfStartDate ? format(parseISO(pdfStartDate), "dd/MM") : "Start date"}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={pdfStartDate ? parseISO(pdfStartDate) : undefined}
                    onSelect={(date) => {
                      if (date) setPdfStartDate(format(date, "yyyy-MM-dd"));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              
              <span className="text-xs">to</span>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 sm:h-6 justify-start text-xs px-2 flex-1 sm:w-28">
                    <CalendarIcon className="mr-1 h-3 w-3" />
                    <span className="hidden sm:inline">{pdfEndDate ? format(parseISO(pdfEndDate), "dd/MM/yyyy") : "End"}</span>
                    <span className="sm:hidden">{pdfEndDate ? format(parseISO(pdfEndDate), "dd/MM") : "End date"}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={pdfEndDate ? parseISO(pdfEndDate) : undefined}
                    onSelect={(date) => {
                      if (date) setPdfEndDate(format(date, "yyyy-MM-dd"));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <Button
              onClick={handleGeneratePdf}
              disabled={generatingPdf || !pdfStartDate || !pdfEndDate}
              variant="outline"
              size="sm"
              className="h-10 sm:h-6 px-3 sm:px-2 text-sm sm:text-xs"
            >
              <Download className="w-4 h-4 sm:w-3 sm:h-3 mr-1" />
              {generatingPdf ? "Generating..." : "Export PDF"}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <Card>
        <CardContent className="p-2 md:p-4">
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : (
            <>
              {viewMode === "gantt" && renderGanttChart()}
              {viewMode === "table" && renderTableView()}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Activity Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Activity</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Site *</Label>
              <Select value={selectedSiteId || ""} onValueChange={setSelectedSiteId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Activity Name *</Label>
              <Input
                value={newActivity.activity_name}
                onChange={(e) => setNewActivity({...newActivity, activity_name: e.target.value})}
                placeholder="e.g., Install electrical conduits"
              />
            </div>
            <div>
              <Label>Contractor *</Label>
              <Select value={newActivity.contractor} onValueChange={(value) => {
                if (value === "__add_new__") {
                  setShowAddContractorDialog(true);
                } else {
                  setNewActivity({...newActivity, contractor: value});
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select contractor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__add_new__" className="text-blue-600 font-semibold">
                    + Add New Contractor
                  </SelectItem>
                  {companies.filter(c => c.company_type === "contractor" || c.company_type === "subcontractor").map(company => (
                    <SelectItem key={company.id} value={company.name}>{company.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Trade *</Label>
              <Select value={newActivity.trade} onValueChange={(value) => {
                if (value === "__add_new__") {
                  setShowAddTradeDialog(true);
                } else {
                  setNewActivity({...newActivity, trade: value});
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select trade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__add_new__" className="text-blue-600 font-semibold">
                    + Add New Trade
                  </SelectItem>
                  {trades.map(trade => (
                    <SelectItem key={trade.id} value={trade.name}>{trade.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={newActivity.start_date}
                onChange={(e) => handleDateChange("start_date", e.target.value, true)}
              />
            </div>
            <div>
              <Label>Duration (days) *</Label>
              <Input
                type="number"
                min="1"
                value={newActivity.duration_days}
                onChange={(e) => handleDateChange("duration_days", e.target.value, true)}
              />
            </div>
            <div>
              <Label>End Date (calculated)</Label>
              <Input
                type="date"
                value={newActivity.end_date}
                disabled
                className="bg-gray-100"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={newActivity.status} onValueChange={(value) => setNewActivity({...newActivity, status: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="delayed">Delayed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={newActivity.notes}
                onChange={(e) => setNewActivity({...newActivity, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddActivity}>Add Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Activity Dialog */}
      {editingActivity && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Activity</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Activity Name *</Label>
                <Input
                  value={editingActivity.activity_name}
                  onChange={(e) => setEditingActivity({...editingActivity, activity_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Contractor *</Label>
                <Select value={editingActivity.contractor} onValueChange={(value) => setEditingActivity({...editingActivity, contractor: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.filter(c => c.company_type === "contractor" || c.company_type === "subcontractor").map(company => (
                      <SelectItem key={company.id} value={company.name}>{company.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Trade *</Label>
                <Select value={editingActivity.trade} onValueChange={(value) => setEditingActivity({...editingActivity, trade: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {trades.map(trade => (
                      <SelectItem key={trade.id} value={trade.name}>{trade.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={editingActivity.start_date}
                  onChange={(e) => handleDateChange("start_date", e.target.value, false)}
                />
              </div>
              <div>
                <Label>Duration (days) *</Label>
                <Input
                  type="number"
                  min="1"
                  value={editingActivity.duration_days || calculateDuration(editingActivity.start_date, editingActivity.end_date)}
                  onChange={(e) => handleDateChange("duration_days", e.target.value, false)}
                />
              </div>
              <div>
                <Label>End Date (calculated)</Label>
                <Input
                  type="date"
                  value={editingActivity.end_date}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              <div>
                <Label>Progress %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={editingActivity.progress_percent}
                  onChange={(e) => setEditingActivity({...editingActivity, progress_percent: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editingActivity.status} onValueChange={(value) => setEditingActivity({...editingActivity, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="delayed">Delayed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={editingActivity.notes}
                  onChange={(e) => setEditingActivity({...editingActivity, notes: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateActivity}>Update Activity</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Contractor Dialog */}
      <Dialog open={showAddContractorDialog} onOpenChange={setShowAddContractorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Contractor</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Contractor Name *</Label>
            <Input
              value={newContractor}
              onChange={(e) => setNewContractor(e.target.value)}
              placeholder="e.g., ABC Construction Ltd"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContractorDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddContractor}>Add Contractor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Trade Dialog */}
      <Dialog open={showAddTradeDialog} onOpenChange={setShowAddTradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Trade</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Trade Name *</Label>
            <Input
              value={newTrade}
              onChange={(e) => setNewTrade(e.target.value)}
              placeholder="e.g., Plumbing, Electrical"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTradeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTrade}>Add Trade</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
